import * as Sentry from "@sentry/react";
import { getSentryFeedbackConfig } from "@/lib/buildInfo";
import { getInitialSentryFeedbackColorScheme } from "@/lib/sentryFeedbackTheme";
import { redactSensitiveQueryString, redactSensitiveUrlParams } from "@/lib/sensitiveUrl";

// ENT-3137 WP1 (VETO C1): public token routes carry a bearer access token in
// the URL query string. Strip it (and any `verify` param) from every URL-bearing
// field of an outgoing Sentry event — request URL/query string, navigation
// breadcrumbs, and the transaction name — so the secret never reaches Sentry.
function scrubSensitiveSentryUrls(event: Sentry.Event): void {
  const request = event.request;
  if (request) {
    const originalUrl = typeof request.url === "string" ? request.url : undefined;
    if (typeof request.url === "string") {
      request.url = redactSensitiveUrlParams(request.url);
    }
    if (typeof request.query_string === "string") {
      request.query_string = redactSensitiveQueryString(request.query_string, originalUrl?.split("?")[0]);
    }
  }

  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      const data = crumb?.data;
      if (!data) {
        continue;
      }
      for (const field of ["to", "from", "url"] as const) {
        const value = data[field];
        if (typeof value === "string") {
          data[field] = redactSensitiveUrlParams(value);
        }
      }
    }
  }

  if (typeof event.transaction === "string") {
    event.transaction = redactSensitiveUrlParams(event.transaction);
  }
}

const dsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE;
const appVersion = __APP_VERSION__;
const buildRef = __APP_BUILD_REF__;
const appSha = __APP_SHA__;
const release = import.meta.env.VITE_SENTRY_RELEASE || `supplyspark-ent@${appVersion}`;
const sentryFeedbackConfig = getSentryFeedbackConfig();

const replayPrivacyConfig = {
  maskAllText: false,
  maskAllInputs: false,
  blockAllMedia: false,
  mask: [
    "input[type='password']",
    "input[autocomplete='current-password']",
    "input[autocomplete='new-password']",
    "input[autocomplete='one-time-code']",
    "input[type='email']",
    "input[type='tel']",
    "input[name*='password' i]",
    "input[name*='email' i]",
    "input[name*='phone' i]",
    "input[name*='otp' i]",
    "input[name*='mfa' i]",
    "input[name*='token' i]",
    "input[name*='secret' i]",
    "input[name*='api' i]",
    "input[name*='key' i]",
    "textarea[name*='token' i]",
    "textarea[name*='secret' i]",
    "textarea[name*='api' i]",
    "textarea[name*='key' i]",
    "[data-sensitive='true']",
  ],
  ignore: [
    "input[type='password']",
    "input[autocomplete='one-time-code']",
    "input[name*='otp' i]",
    "input[name*='mfa' i]",
    "input[name*='token' i]",
    "input[name*='secret' i]",
    "input[name*='api' i]",
    "input[name*='key' i]",
  ],
};

const defaultSampleRateProfiles = {
  development: {
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1,
  },
  preview: {
    tracesSampleRate: 1,
    replaysSessionSampleRate: 0.25,
    replaysOnErrorSampleRate: 1,
  },
  staging: {
    tracesSampleRate: 1,
    replaysSessionSampleRate: 1,
    replaysOnErrorSampleRate: 1,
  },
  production: {
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1,
  },
} as const;

type SampleRateProfile = (typeof defaultSampleRateProfiles)[keyof typeof defaultSampleRateProfiles];

function isLocalSentryEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_LOCAL_SENTRY === "true";
}

function isLocalBrowserRuntime(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function shouldDisableLocalSentry(): boolean {
  if (isLocalSentryEnabled()) {
    return false;
  }

  const normalizedEnvironment = environment.toLowerCase();
  const localLikeEnvironment =
    normalizedEnvironment === "development" || normalizedEnvironment === "local";

  return localLikeEnvironment && isLocalBrowserRuntime();
}

function resolveSampleRateProfile(currentEnvironment: string): SampleRateProfile {
  const normalizedEnvironment = currentEnvironment.toLowerCase();

  return (
    defaultSampleRateProfiles[normalizedEnvironment as keyof typeof defaultSampleRateProfiles] ??
    defaultSampleRateProfiles.production
  );
}

function resolveSampleRate(rawValue: string | undefined, fallback: number): number {
  const parsedValue = Number.parseFloat(rawValue ?? "");
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

export function initSentry(): void {
  if (!dsn || shouldDisableLocalSentry()) {
    return;
  }

  const sampleRateProfile = resolveSampleRateProfile(environment);
  const tracesSampleRate = resolveSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    sampleRateProfile.tracesSampleRate,
  );
  const replaysSessionSampleRate = resolveSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    sampleRateProfile.replaysSessionSampleRate,
  );
  const replaysOnErrorSampleRate = resolveSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    sampleRateProfile.replaysOnErrorSampleRate,
  );

  Sentry.init({
    dsn,
    environment,
    release,
    initialScope: {
      tags: {
        app_version: appVersion,
        build_ref: buildRef,
        app_sha: appSha,
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(replayPrivacyConfig),
      Sentry.feedbackIntegration({
        autoInject: false,
        showBranding: false,
        colorScheme: getInitialSentryFeedbackColorScheme(),
        ...sentryFeedbackConfig,
      }),
    ],
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.vercel\.app/,
      /^https:\/\/.*\.supabase\.co/,
/^https:\/\/(?:[a-z0-9-]+\.)*supply-guard\.io(?::\d+)?(?:[/?#]|$)/,
    ],
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    beforeSend(event) {
      scrubSensitiveSentryUrls(event);
      return event;
    },
    beforeSendTransaction(event) {
      scrubSensitiveSentryUrls(event);
      return event;
    },
  });
}
